/**
 * Looks (ADR-0073, ADR-0088, ADR-0090): what a gardener is drawn as: skin, hairstyle, hair colour, eyes, mouth, facial
 * hair, extras and the outfit; the hat (HATS) is worn over it. The face is never picked slot by slot: it comes with a
 * head (`HEADS`, ADR-0112), the outfit on its own. Every option is drawn in assets (`@gardener/assets/gardener`, which
 * may not import content, so the keys are duplicated there: keep them in the same order; a client test checks). An
 * account without a look of its own wears the one its id picks (`accountLook`, heads.ts), so nobody starts as a clone
 * of everyone else.
 */
export const LOOKS = {
  skin: {
    name: 'Skin',
    options: {
      peach: 'Peach',
      fair: 'Fair',
      ivory: 'Ivory',
      tan: 'Tan',
      bronze: 'Bronze',
      brown: 'Brown',
      umber: 'Umber',
      deep: 'Deep',
    },
  },
  hair: {
    name: 'Hairstyle',
    options: {
      bob: 'Bob',
      short: 'Short',
      pigtails: 'Pigtails',
      long: 'Long',
      bun: 'Bun',
      curly: 'Curly',
      afro: 'Afro',
      braids: 'Braids',
      locs: 'Locs',
      buzz: 'Buzz cut',
      topknot: 'Top knot',
      puffs: 'Afro puffs',
      sleek: 'Sleek',
    },
  },
  hairColour: {
    name: 'Hair colour',
    options: {
      chestnut: 'Chestnut',
      black: 'Black',
      blonde: 'Blonde',
      ginger: 'Ginger',
      silver: 'Silver',
      pink: 'Pink',
    },
  },
  eyes: {
    name: 'Eyes',
    options: {
      round: 'Round',
      lashes: 'Lashes',
      sleepy: 'Sleepy',
      happy: 'Happy',
      monolid: 'Monolid',
      almond: 'Almond',
    },
  },
  mouth: { name: 'Mouth', options: { smile: 'Smile', grin: 'Grin', cat: 'Cat', tongue: 'Tongue' } },
  beard: {
    name: 'Beard',
    options: {
      none: 'None',
      stubble: 'Stubble',
      moustache: 'Moustache',
      pencil: 'Pencil',
      handlebar: 'Handlebar',
      goatee: 'Goatee',
      full: 'Full',
    },
  },
  extras: {
    name: 'Extras',
    options: { none: 'None', shades: 'Shades', glasses: 'Glasses', earring: 'Earring', chain: 'Chain' },
  },
  outfit: {
    name: 'Outfit',
    options: {
      overalls: 'Overalls',
      hoodie: 'Hoodie',
      shirt: 'Shirt',
      // The sets' suits (ADR-0112), keyed like their set: bought, never seeded (`lookOf`).
      gnome: 'Gnome tunic',
      beekeeper: 'Beekeeper suit',
      astronaut: 'Space suit',
      pumpkin: 'Pumpkin patch',
      // A season's suit (ADR-0123): only the pass gives it.
      reaper: 'Reaper robe',
      // The Creator Program's suit (ADR-0138, content `PROGRAM_ITEMS`): only its milestones give it.
      creator: 'Creator jacket',
      // The mythic sets' suits (ADR-0160; those in preview: content `SETS`).
      inferno: 'Cinder mail',
      kitsune: 'Shrine robe',
      voidwalker: 'Starfield suit',
      tempest: 'Storm plate',
      glacier: 'Rime plate',
      angler: 'Diving suit',
      jester: 'Motley',
      plague: 'Oilskin coat',
      chronomancer: 'Gear waistcoat',
      glitch: 'Techwear',
      disco: 'Sequin jumpsuit',
      origami: 'Folded armour',
      ooze: 'Goo-drenched suit',
      barnstormer: 'Bomber jacket',
      pirate: "Captain's coat",
      magician: 'Tailcoat',
      chef: "Chef's whites",
      knight: 'Plate and tabard',
      sheriff: "Sheriff's duster",
      luchador: 'Singlet and title belt',
    },
  },
} as const satisfies Record<string, { name: string; options: Record<string, string> }>;

export type LookSlot = keyof typeof LOOKS;
export type Look = { [S in LookSlot]: keyof (typeof LOOKS)[S]['options'] };

/**
 * The slots, in the order the gardener screen shows them and a packed look stores them (a new slot goes last, so a
 * packed look keeps its bits).
 */
export const LOOK_SLOTS = Object.keys(LOOKS) as LookSlot[];

/** A slot's options in order: an option on the wire is its index here. */
export const lookOptions = <S extends LookSlot>(slot: S): Look[S][] => Object.keys(LOOKS[slot].options) as Look[S][];

/**
 * Every slot's first option: the art as first drawn (what a look this build can't read falls back to): no beard or
 * extras, the overalls.
 */
export const DEFAULT_LOOK: Look = Object.fromEntries(LOOK_SLOTS.map((s) => [s, lookOptions(s)[0]])) as Look;

/**
 * Bits per slot in a packed look (ADR-0090 amended): room for 2^bits options each, 29 of the 32 bits. The outfits
 * outgrew 4 bits with the mythic sets (ADR-0160 amended); a slot that outgrows its own is a wire change (the protocol's
 * version) and takes from the 3 bits left.
 */
const BITS: Record<LookSlot, number> = {
  skin: 4,
  hair: 4,
  hairColour: 3,
  eyes: 3,
  mouth: 3,
  beard: 3,
  extras: 3,
  outfit: 6,
};
/** Where each slot's bits start, in `LOOK_SLOTS`' order. */
const SHIFT = LOOK_SLOTS.map((_, i) => LOOK_SLOTS.slice(0, i).reduce((at, s) => at + BITS[s], 0));

/**
 * `look` in one number (the roster's `look`, ADR-0073): each slot's option index in its `BITS`, the slots in order from
 * the lowest bit. The shifts are signed, so the result is made unsigned (`>>> 0`) and read with `>>>`.
 */
export function packLook(look: Look): number {
  return (
    LOOK_SLOTS.reduce((n, s, i) => n | (Math.max(0, lookOptions(s).indexOf(look[s])) << (SHIFT[i] as number)), 0) >>> 0
  );
}

/** The look packed in `n`; an option this build doesn't know is the slot's first. */
export function unpackLook(n: number): Look {
  return Object.fromEntries(
    LOOK_SLOTS.map((s, i) => {
      const options = lookOptions(s);
      return [s, options[(n >>> (SHIFT[i] as number)) & ((1 << BITS[s]) - 1)] ?? options[0]];
    }),
  ) as Look;
}

/**
 * Whether `x` is a look this build can read (a request body, a stored one): every key one of its slots, each one of
 * the slot's options. A slot left out (a body from a build before it) is its first: `accountLook(id, x)` fills it in.
 */
export function isLook(x: unknown): x is Partial<Look> {
  if (typeof x !== 'object' || x === null) return false;
  return Object.entries(x).every(
    ([s, o]) => Object.hasOwn(LOOKS, s) && typeof o === 'string' && Object.hasOwn(LOOKS[s as LookSlot].options, o),
  );
}
